import React, { useState } from "react";
import { Download, Link2, Copy, Check, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useGov } from "../../governance/GovContext";
import { saveCombined } from "../../governance/govPdf";
import { createReport, emailReport } from "../../governance/govShare";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

export default function ReportShare() {
  const gov = useGov();
  const [link, setLink] = useState("");
  const [copied, setCopied] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", consent: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const ensureReport = async () => {
    if (link) return link;
    const { url } = await createReport(gov);
    setLink(url);
    return url;
  };

  const share = async () => {
    setSharing(true);
    try {
      const url = await ensureReport();
      await navigator.clipboard.writeText(url).catch(() => {});
      setCopied(true);
      toast.success("Private link created and copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      toast.error("Could not create link. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(link).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!form.email || !form.consent) { toast.error("Please add your email and tick consent."); return; }
    setSending(true);
    try {
      const url = await ensureReport();
      const id = url.split("/r/")[1];
      await emailReport(id, form);
      setSent(true);
      toast.success("Report on its way — check your inbox.");
    } catch (err) {
      toast.error("Could not send the email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-xl border border-primary/25 bg-primary/[0.06] p-4" data-testid="gov-report-share">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-foreground">Save, share or email everything you've completed this session.</p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => saveCombined(gov)} data-testid="gov-download-full-report-overview" className="bg-primary text-primary-foreground hover:bg-primary/90">
            <Download className="mr-1.5 h-4 w-4" /> Download PDF
          </Button>
          <Button size="sm" variant="outline" onClick={share} disabled={sharing} data-testid="gov-share-link" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
            {sharing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Link2 className="mr-1.5 h-4 w-4" />} Share link
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowEmail((s) => !s)} data-testid="gov-email-toggle" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
            <Mail className="mr-1.5 h-4 w-4" /> Email me this report
          </Button>
        </div>
      </div>

      {link && (
        <div className="mt-3 flex items-center gap-2" data-testid="gov-share-link-row">
          <Input readOnly value={link} className="bg-navy-800 border-white/12 text-sm" data-testid="gov-share-link-input" />
          <Button size="sm" variant="outline" onClick={copy} className="shrink-0 border-white/15 bg-transparent hover:bg-white/5 text-foreground">
            {copied ? <Check className="h-4 w-4 text-signal-green" /> : <Copy className="h-4 w-4" />}
          </Button>
        </div>
      )}

      {showEmail && !sent && (
        <form onSubmit={submitEmail} className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="gov-email-form">
          <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-navy-800 border-white/12" data-testid="gov-email-name" />
          <Input type="email" required placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-navy-800 border-white/12" data-testid="gov-email-address" />
          <label className="sm:col-span-2 flex items-start gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5" data-testid="gov-email-consent" />
            <span>I agree to receive this report and for norse3 to contact me about it. Educational only — not advice.</span>
          </label>
          <div className="sm:col-span-2">
            <Button type="submit" size="sm" disabled={sending} data-testid="gov-email-submit" className="bg-primary text-primary-foreground hover:bg-primary/90">
              {sending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />} Send me the report
            </Button>
          </div>
        </form>
      )}

      {sent && (
        <p className="mt-3 text-sm text-signal-green" data-testid="gov-email-sent">Sent — check your inbox for a secure link to your report.</p>
      )}
    </div>
  );
}
